import {
  OutreachConfigResponse,
  OutreachConfigResponseWebhook,
  OutreachSubscriberConfigResponse,
  OutreachTriggerTypes,
  SupportOutreachTriggerFormFields,
} from '../../Monitor/types';
import { FrequencyUnits } from '../../Monitor/components/OutreachConfigForm/helpers';

const isOneTimeOnly = (
  response: Pick<
    OutreachConfigResponse | OutreachSubscriberConfigResponse,
    'triggerRelatedInfo'
  >,
): boolean => {
  const { schedule } = response.triggerRelatedInfo;
  if (!schedule) {
    return false;
  }
  return (
    schedule.recurrenceUnit === FrequencyUnits.Once && schedule.recurrence === 0
  );
};

const renderMaxFrequencyUnit = (unit: string): FrequencyUnits =>
  unit?.toLowerCase() === 'hr'
    ? FrequencyUnits.Hour
    : (unit as FrequencyUnits) || FrequencyUnits.Hour;

export const getTriggerSection = (
  response: OutreachConfigResponse | OutreachSubscriberConfigResponse,
) => {
  const common = {
    [SupportOutreachTriggerFormFields.TriggerType]: response?.triggerType,
    [SupportOutreachTriggerFormFields.IsOneTimeOnly]: false,
    [SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreach]:
      response?.maxNotificationsAllowed?.maxAllowed || 0,
    [SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachEvery]:
      response?.maxNotificationsAllowed?.recurrence || 0,
    [SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachUnit]: renderMaxFrequencyUnit(
      response?.maxNotificationsAllowed?.recurrenceUnit,
    ),
    [SupportOutreachTriggerFormFields.SendClearEmailWhenIssueCleared]: Boolean(
      response?.notifyIssueClearMessage,
    ),
    [SupportOutreachTriggerFormFields.IssueClearedWhen]: undefined,
    [SupportOutreachTriggerFormFields.IssueTriggeredWhen]: undefined,
  };
  if (response?.triggerType === OutreachTriggerTypes.Time) {
    return {
      ...common,
      [SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachUnit]: renderMaxFrequencyUnit(
        response?.maxNotificationsAllowed?.recurrenceUnit,
      ),
      [SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreach]:
        response.maxNotificationsAllowed.maxAllowed,
      [SupportOutreachTriggerFormFields.ScheduleMaxFrequencyOfSupportOutreachEvery]:
        response.maxNotificationsAllowed.recurrence,
      [SupportOutreachTriggerFormFields.SendClearEmailWhenIssueCleared]: false,
      [SupportOutreachTriggerFormFields.IsOneTimeOnly]: isOneTimeOnly(response),
      [SupportOutreachTriggerFormFields.TimeRepeatEvery]:
        response.triggerRelatedInfo.schedule.recurrence,
      [SupportOutreachTriggerFormFields.TimeRepeatWeekdays]:
        response?.triggerRelatedInfo?.schedule?.weekdays || [],
      [SupportOutreachTriggerFormFields.TimeRepeatEveryUnit]: renderMaxFrequencyUnit(
        response?.triggerRelatedInfo?.schedule?.recurrenceUnit,
      ),
    };
  }
  if (
    [
      OutreachTriggerTypes.MaxPercentage,
      OutreachTriggerTypes.MaxIncreaseRate,
    ].includes(response?.triggerType)
  ) {
    return {
      ...common,
      [SupportOutreachTriggerFormFields.IssueClearedWhen]:
        response.triggerRelatedInfo.issueClearingLocationPercentage,
      [SupportOutreachTriggerFormFields.IssueTriggeredWhen]:
        response.triggerRelatedInfo.issueTriggeringLocationPercentage,
    };
  }
  if (response?.triggerType === OutreachTriggerTypes.MaxValue) {
    return {
      ...common,
      [SupportOutreachTriggerFormFields.IssueClearedWhen]:
        response?.triggerRelatedInfo?.issueClearingLocationCount || 0,
      [SupportOutreachTriggerFormFields.IssueTriggeredWhen]:
        response?.triggerRelatedInfo?.issueTriggeringLocationCount || 0,
    };
  }
  if (response?.triggerType === OutreachTriggerTypes.CSPOutageDetected) {
    return {
      ...common,
      [SupportOutreachTriggerFormFields.IssueTriggeredWhen]:
        response?.triggerRelatedInfo?.minSubscribersAffected || 0,
      [SupportOutreachTriggerFormFields.IssueClearedWhen]:
        response?.triggerRelatedInfo?.issueClearedPercentage || 0,
    };
  }
};

export const getWebhookTriggerSection = (
  response: OutreachConfigResponseWebhook,
) => {
  const common = {
    [SupportOutreachTriggerFormFields.TriggerType]:
      response?.triggerConfig.triggerType,
    [SupportOutreachTriggerFormFields.IsOneTimeOnly]: false,
    [SupportOutreachTriggerFormFields.SendClearEmailWhenIssueCleared]: Boolean(
      response?.notifyIssueClearMessage,
    ),
    [SupportOutreachTriggerFormFields.IssueClearedWhen]: undefined,
    [SupportOutreachTriggerFormFields.IssueTriggeredWhen]: undefined,
  };
  if (response?.triggerConfig.triggerType === OutreachTriggerTypes.Time) {
    return {
      ...common,
      [SupportOutreachTriggerFormFields.SendClearEmailWhenIssueCleared]: false,
      [SupportOutreachTriggerFormFields.IsOneTimeOnly]: isOneTimeOnly(
        response?.triggerConfig,
      ),
      [SupportOutreachTriggerFormFields.TimeRepeatEvery]:
        response?.triggerConfig.triggerRelatedInfo.schedule.recurrence,
      [SupportOutreachTriggerFormFields.TimeRepeatWeekdays]:
        response?.triggerConfig?.triggerRelatedInfo?.schedule?.weekdays || [],
      [SupportOutreachTriggerFormFields.TimeRepeatEveryUnit]: renderMaxFrequencyUnit(
        response?.triggerConfig?.triggerRelatedInfo?.schedule?.recurrenceUnit,
      ),
    };
  }
  if (
    [
      OutreachTriggerTypes.MaxPercentage,
      OutreachTriggerTypes.MaxIncreaseRate,
    ].includes(response?.triggerConfig.triggerType)
  ) {
    return {
      ...common,
      [SupportOutreachTriggerFormFields.IssueClearedWhen]:
        ('issueClearingLocationPercentage' in
          response?.triggerConfig.triggerRelatedInfo &&
          response?.triggerConfig.triggerRelatedInfo
            .issueClearingLocationPercentage) ||
        0,
      [SupportOutreachTriggerFormFields.IssueTriggeredWhen]:
        ('issueTriggeringLocationPercentage' in
          response?.triggerConfig.triggerRelatedInfo &&
          response?.triggerConfig.triggerRelatedInfo
            .issueTriggeringLocationPercentage) ||
        0,
    };
  }
  if (response?.triggerConfig.triggerType === OutreachTriggerTypes.MaxValue) {
    return {
      ...common,
      [SupportOutreachTriggerFormFields.IssueClearedWhen]:
        response?.triggerConfig?.triggerRelatedInfo
          ?.issueClearingLocationCount || 0,
      [SupportOutreachTriggerFormFields.IssueTriggeredWhen]:
        response?.triggerConfig?.triggerRelatedInfo
          ?.issueTriggeringLocationCount || 0,
    };
  }
  if (
    response?.triggerConfig.triggerType ===
    OutreachTriggerTypes.CSPOutageDetected
  ) {
    return {
      ...common,
      [SupportOutreachTriggerFormFields.IssueTriggeredWhen]:
        response?.triggerConfig?.triggerRelatedInfo?.minSubscribersAffected ||
        0,
      [SupportOutreachTriggerFormFields.IssueClearedWhen]:
        response?.triggerConfig?.triggerRelatedInfo?.issueClearedPercentage ||
        0,
    };
  }
};
